/**
 * Route prefixes where the mobile app chrome (the Mucka Bar)
 * must NOT render. These are surfaces that are either pre-auth, marketing, or
 * — critically — public **customer-facing** pages a homeowner opens without
 * ever logging into the trades app.
 *
 * `MobileNav` is mounted in the ROOT layout, so
 * they'd otherwise render on every route. They used to keep two hand-copied
 * lists that drifted apart — which is how `/q` (the customer's live-quote page)
 * ended up showing the app's tab bar on a document meant for the customer.
 * One list now; add new public routes here.
 */
export const CHROME_HIDDEN_PATHS = [
  "/login",
  "/signup",
  "/auth",
  "/admin",
  "/invite",
  "/onboarding",
  "/welcome", // the invited-member onboarding flow — same reason as /onboarding
  "/pending",
  // The team hard-stop. Every tab on the bar goes somewhere this owner is not
  // allowed yet — the whole point of the page is that the account is held —
  // so the bar is a row of controls that do nothing, sitting under a screen
  // that says "one last thing". Same reasoning as /pending above it.
  "/setup-call",
  "/offline",
  "/certs-offline", // the cert shell — same surface, served under its own route
  "/ui-demo",
  "/workspaces",
  "/help",
  "/pricing", // public marketing page — no app chrome for logged-out visitors
  "/q", // customer's live-quote page (public, unguessable short_id)
  "/pay", // customer's invoice page (public, unguessable short_id)
  "/card", // customer saves a card to pay after the job (public, unguessable short_id)
  // The online booking page. The bar does not check for a session, so every
  // homeowner booking on a phone got Mucka's Home / Diary / Ask Mucka tabs
  // under the trade's booking form — reported from inside an owner's own
  // website embed on 2026-09-25.
  "/book",
  "/plan", // customer's service-plan sign-up page — the same miss as /book
  "/v", // a tenant's pick-a-time link for one visit — same page family as /book
  "/agency", // the ads agency's view of Mission Control (public, unguessable token)
  "/plan-offer", // a standalone plan offer from a campaign email — same page family as /plan
  "/oauth", // the consent screen a third-party app sends somebody to
  "/integrations/oauth-complete", // the provider pop-up's landing page — not /integrations itself
  // A workspace held for billing or maintenance: every tab is redirected
  // straight back here, so the bar is a row of controls that do nothing.
  "/paywall",
  "/workspace-paused",
  "/maintenance",
] as const;

/**
 * True when the app's mobile chrome should be hidden on this pathname.
 *
 * Matches on a path-SEGMENT boundary, not a raw string prefix: `/q` hides
 * `/q` and `/q/<id>` but must NOT hide `/quotes` (the owner's authed quote
 * list). A bare `startsWith` would swallow it.
 */
export function hidesAppChrome(pathname: string): boolean {
  return (
    CHROME_HIDDEN_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/")) ||
    // The `(print)` route group: `/quotes/<id>/print`, `/invoices/<id>/print`,
    // `/invoices/history/<id>/print` — the paper copy, not an app screen.
    pathname.endsWith("/print")
  );
}

/**
 * The top-level route segments whose HTML the service worker hands out in
 * place of OTHER routes when a navigation fails (`public/sw.js`: `/offline`,
 * and `/certs-offline` for certificate and calculator paths).
 *
 * 🚨 This is asked of the route TREE (`useSelectedLayoutSegment()`), never of
 * the pathname. The address bar keeps the URL that was asked for, so
 * `hidesAppChrome(pathname)` says "draw the bar" on `/clients` while the HTML
 * it is hydrating was rendered for `/offline` without one — MUCKA-WEB-AY. The
 * tree comes from the payload embedded in that same HTML, so it agrees with
 * the server by construction.
 */
export const OFFLINE_SHELL_SEGMENTS = ["offline", "certs-offline"] as const;

export function isOfflineShellSegment(segment: string | null | undefined): boolean {
  return !!segment && (OFFLINE_SHELL_SEGMENTS as readonly string[]).includes(segment);
}
